/**
 * Viewport model for the detection canvas.
 *
 * The fabric canvas element always fills its container. The image lives in
 * canvas coordinate space at its ORIGINAL pixel size and everything the user
 * sees is a viewport transform on top of that. This is what makes a 96x96
 * thumbnail and a 4000x3000 photo equally workable: `fitToViewport` scales the
 * image up or down until it fills the panel instead of drawing it at one
 * device pixel per image pixel (which is why small images used to appear as a
 * postage stamp in the middle of an empty panel).
 *
 * Coordinate spaces
 *   image space  – annotations, background image, fabric objects (px of the original image)
 *   screen space – the canvas element (CSS px). screen = image * zoom + translate
 */

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 40;
export const ZOOM_STEP = 1.25;
/** Breathing room kept around the image when fitting, in screen px. */
export const FIT_PADDING = 16;
/** Above this zoom the image is drawn unsmoothed so pixels stay honest. */
const SMOOTHING_CUTOFF = 2.5;

export const clampZoom = (zoom) => {
  const value = Number(zoom);
  if (!Number.isFinite(value) || value <= 0) return 1;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
};

export const getImageSize = (image) => ({
  width: Number(image?.originalWidth) || 0,
  height: Number(image?.originalHeight) || 0,
});

/** Zoom at which the image exactly fits the canvas, upscaling small images. */
export const getFitZoom = (canvas, image, padding = FIT_PADDING) => {
  if (!canvas) return 1;
  const { width: iw, height: ih } = getImageSize(image);
  const vw = canvas.getWidth();
  const vh = canvas.getHeight();
  if (!iw || !ih || !vw || !vh) return 1;
  const availableW = Math.max(1, vw - padding * 2);
  const availableH = Math.max(1, vh - padding * 2);
  return clampZoom(Math.min(availableW / iw, availableH / ih));
};

/**
 * Keep the image anchored sensibly: centred on any axis where it is smaller
 * than the viewport, and otherwise never dragged further than `slack` px past
 * the edge so it can't be lost off-screen.
 */
const clampAxis = (translate, viewport, content) => {
  if (!content) return translate;
  if (content <= viewport) return (viewport - content) / 2;
  const slack = Math.min(96, viewport * 0.25);
  const min = viewport - content - slack;
  const max = slack;
  return Math.min(max, Math.max(min, translate));
};

/** Stroke widths are stored in image space, so divide to keep them ~constant on screen. */
export const applyStrokeScale = (canvas, zoom, baseStroke = 2) => {
  if (!canvas) return;
  const width = baseStroke / clampZoom(zoom);
  canvas.getObjects().forEach((obj) => {
    if (obj.isGuide) return; // crosshair guides own their hairline width
    if (obj.type === 'rect' || obj.type === 'polygon') {
      obj.set('strokeWidth', width);
    }
  });
};

/** Write a transform to the canvas, clamped so the image stays reachable. */
export const applyViewport = (canvas, zoom, translateX, translateY, image) => {
  if (!canvas) return 1;
  const z = clampZoom(zoom);
  const { width: iw, height: ih } = getImageSize(image);
  const x = clampAxis(translateX, canvas.getWidth(), iw * z);
  const y = clampAxis(translateY, canvas.getHeight(), ih * z);

  const background = canvas.backgroundImage;
  if (background) {
    const smooth = z < SMOOTHING_CUTOFF;
    if (background.imageSmoothing !== smooth) {
      background.imageSmoothing = smooth;
      background.dirty = true;
    }
  }

  canvas.setViewportTransform([z, 0, 0, z, x, y]);
  canvas.requestRenderAll();
  return z;
};

/** Centre the image and scale it to fill the panel. Returns the new zoom. */
export const fitToViewport = (canvas, image, baseStroke = 2, padding = FIT_PADDING) => {
  if (!canvas) return 1;
  const zoom = getFitZoom(canvas, image, padding);
  const { width: iw, height: ih } = getImageSize(image);
  applyStrokeScale(canvas, zoom, baseStroke);
  return applyViewport(
    canvas,
    zoom,
    (canvas.getWidth() - iw * zoom) / 2,
    (canvas.getHeight() - ih * zoom) / 2,
    image
  );
};

/** Zoom keeping `point` (screen px, defaults to the viewport centre) pinned. */
export const zoomAtPoint = (canvas, image, nextZoom, point, baseStroke = 2) => {
  if (!canvas) return 1;
  const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
  const current = vpt[0] || 1;
  const zoom = clampZoom(nextZoom);
  const anchor = point || { x: canvas.getWidth() / 2, y: canvas.getHeight() / 2 };
  const ratio = zoom / current;
  applyStrokeScale(canvas, zoom, baseStroke);
  return applyViewport(
    canvas,
    zoom,
    anchor.x - (anchor.x - vpt[4]) * ratio,
    anchor.y - (anchor.y - vpt[5]) * ratio,
    image
  );
};

/** Zoom to 1 image pixel per screen pixel, keeping the viewport centre. */
export const zoomToActualSize = (canvas, image, baseStroke = 2) =>
  zoomAtPoint(canvas, image, 1, null, baseStroke);

export const panViewport = (canvas, image, deltaX, deltaY) => {
  if (!canvas) return 1;
  const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
  return applyViewport(canvas, vpt[0], vpt[4] + deltaX, vpt[5] + deltaY, image);
};

/** Screen point -> image point (fabric's getPointer needs a DOM event). */
export const screenToImage = (canvas, x, y) => {
  const vpt = canvas?.viewportTransform || [1, 0, 0, 1, 0, 0];
  const zoom = vpt[0] || 1;
  return { x: (x - vpt[4]) / zoom, y: (y - vpt[5]) / zoom };
};

export const getViewportState = (canvas) => {
  const vpt = canvas?.viewportTransform || [1, 0, 0, 1, 0, 0];
  return { zoom: vpt[0] || 1, x: vpt[4], y: vpt[5] };
};
